import { useUpdateSearchParamUrl } from 'hooks/hook';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Location } from 'types/loctions';
import { SIZE_LOCATIONS_PAGE } from 'const/paging';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getLocations } from 'services/locations';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { isNil, omitBy } from 'lodash';
import { RoomInfo } from 'types/room';
import { isRoomLocationLevel, renderTitle } from 'utils/renderTitleLocation';
import * as qs from 'qs';
import { deleteLocation } from 'services/clients';
import { useFlash } from 'contexts/flash';
import { StorageServices } from 'services/storage';
const storage = new StorageServices();
export const useInjection = () => {
  const { clientId, locationId, clientName } = useParams();
  const { setFlash } = useFlash();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useUpdateSearchParamUrl();
  const [locations, setLocations] = useState<Location[]>([]);
  const [locationDelete, setLocationDelete] = useState<Location>();
  const [locationReparent, setLocationReparent] = useState<Location>();
  const [locationRename, setLocationRename] = useState<Location>();
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [roomInfo, setRoomInfo] = useState<RoomInfo>([]);
  const [searchType, setSearchType] = useState<string>('partial-text');
  const [title, setTitle] = useState<{
    title: string;
    textButton: string;
    level: number;
  }>({
    title: '',
    textButton: '',
    level: 0
  });

  const isFreeSearch = useMemo(() => {
    return !!searchParams.get('searchText');
  }, [searchParams]);

  const header = [{ label: 'Location Name' }, { label: 'Actions', width: 420 }];
  const handleGetInitLocations = useCallback(async () => {
    if (!clientId) return;
    const params = {
      size: SIZE_LOCATIONS_PAGE,
      page: 1,
      keyword: searchParams.get('searchText') || undefined,
      orderBy: searchParams.get('sortBy') || undefined,
      order: searchParams.get('order') || undefined,
      type: searchParams.get('type') || undefined,
      parentId: searchParams.get('searchText') ? undefined : locationId || undefined,
      clientId: clientId ? clientId : undefined,
      searchType
    };
    const data = (await getLocations(params)) as {
      locations: Location[];
      room: RoomInfo;
      total: number;
    };
    setLocations(data.locations);
    setRoomInfo(data.room);
    const levelLocationInit =
      data?.room?.find((item) => item.id === Number(locationId))?.level || 0;
    setTitle(
      renderTitle(data.locations[0]?.level ? data.locations[0]?.level : levelLocationInit + 1)
    );
    const element = document.getElementById('id-table-clients');
    if (element) {
      element.scrollTo({ top: 0, behavior: 'smooth' });
    }
    if (data.total > SIZE_LOCATIONS_PAGE) {
      setPage(2);
      setHasMore(true);
      return;
    }
    setHasMore(false);
  }, [clientId, locationId, searchParams, searchType]);
  const paths: RoomInfo = useMemo(() => {
    return [
      { name: 'Clients', level: 999, onClick: () => navigate('/clients') },
      {
        name: clientName || '',
        id: Number(clientId),
        isClient: true,
        level: 998,
        onClick: () => navigate(`/clients/${clientName}/${clientId}/location`)
      },
      ...roomInfo
        .sort((a, b) => a.level - b.level)
        .map((item) => {
          const href = isRoomLocationLevel(item.level)
            ? `/clients/${clientName}/${clientId}/location/${item.id}/room`
            : `/clients/${clientName}/${clientId}/location/${item.id}`;
          return {
            ...item,
            onClick: () => navigate(href)
          };
        })
    ];
  }, [clientId, clientName, navigate, roomInfo]);

  const fetchMoreData = useCallback(async () => {
    const params = {
      size: SIZE_LOCATIONS_PAGE,
      page: page, // Use the current page
      keyword: searchParams.get('searchText') || undefined,
      orderBy: searchParams.get('sortBy') || undefined,
      order: searchParams.get('order') || undefined,
      type: searchParams.get('type') || undefined,
      parentId: locationId || undefined,
      clientId: !locationId && clientId ? clientId : undefined,
      searchType
    };
    const data = await getLocations(params);
    setLocations((prevState) => [...prevState, ...data.locations]);
    if (data.total > SIZE_LOCATIONS_PAGE * page) {
      setPage((prevState) => prevState + 1);
      setHasMore(true);
      return;
    }
    setHasMore(false);
  }, [clientId, locationId, page, searchParams, searchType]);

  const { asyncCallback: asyncGetInitLocations } = useAsyncCallback(handleGetInitLocations, []);

  const handleSearch = useCallback(
    (searchText: string) => {
      setSearchParams(omitBy({ searchText }, isNil) as { [key: string]: string });
    },
    [setSearchParams]
  );

  const filterByTypeAccess = useCallback(
    (type: string) => {
      setSearchParams(omitBy({ type }, isNil) as { [key: string]: string });
    },
    [setSearchParams]
  );

  useEffect(() => {
    asyncGetInitLocations();
  }, [asyncGetInitLocations]);

  const handleBack = useCallback(() => {
    if (!locationId) {
      return navigate('/clients');
    }

    const pathSegments = location.pathname.split('/');
    if (pathSegments.length > 1) {
      pathSegments.pop();
      navigate(pathSegments.join('/'));
    }
  }, [location.pathname, locationId, navigate]);

  const onSortHeader = useCallback(
    (sortField: string) => {
      let order = 'asc';
      const sortFieldOld = searchParams.get('sortBy');
      const orderOld = searchParams.get('order');
      if (sortFieldOld === sortField) {
        if (orderOld === 'asc') {
          order = 'desc';
        } else {
          order = 'asc';
        }
      }
      setSearchParams(
        omitBy({ sortBy: sortField, order: order }, isNil) as { [key: string]: string }
      );
    },
    [searchParams, setSearchParams]
  );
  const onCreate = useCallback(
    (level: number) => {
      const query = qs.stringify({
        level
      });
      navigate(`${location.pathname}/create?${query}`);
    },
    [navigate, location.pathname]
  );

  const onClickAction = useCallback(
    ({ href }: { href: string; path: string; locationId: number }) => {
      navigate(href);
    },
    [navigate]
  );

  const listButton = useMemo(() => {
    const buttons = [];
    buttons.push({
      textButton: title.textButton,
      onClick: () => onCreate(title.level),
      type: 'primary'
    });

    if ((title.level === 3 || title.level === 2) && locations.length === 0) {
      buttons.push({
        textButton: 'Add Vertical Level',
        onClick: () => onCreate(4),
        type: 'primary'
      });
    }
    if (title.level !== 5) {
      buttons.push({
        textButton: 'Add Location',
        onClick: () => onCreate(5),
        type: 'primary'
      });
    }

    return buttons;
  }, [locations.length, onCreate, title.level, title.textButton]);

  const handleDeleteLocation = useCallback(async () => {
    if (!locationDelete?.id) return;
    await deleteLocation(locationDelete.id);
    setFlash({ type: 'success', message: 'Delete locations successful' });
    setLocations((prevState) => prevState.filter((item) => item.id !== locationDelete.id));
    setLocationDelete(undefined);
  }, [locationDelete?.id, setFlash]);

  const { asyncCallback: asyncDeleteLocation } = useAsyncCallback(handleDeleteLocation, []);

  const handleNavigateLastRoom = useCallback(() => {
    const path = storage.getLastRoomPath(clientId);
    if (path) navigate(path);
  }, [clientId, navigate]);

  return {
    locations,
    setLocations,
    fetchMoreData,
    hasMore,
    handleSearch,
    onSortHeader,
    filterByTypeAccess,
    searchParams,
    pathName: location.pathname,
    handleBack,
    onCreate,
    paths,
    onClickAction,
    clientId,
    clientName,
    title,
    header,
    listButton,
    locationDelete,
    setLocationDelete,
    locationReparent,
    setLocationReparent,
    locationRename,
    setLocationRename,
    isFreeSearch,
    handleDeleteLocation: asyncDeleteLocation,
    pathLastRoom: storage.getLastRoomPath(clientId),
    handleNavigateLastRoom,
    searchType,
    setSearchType,
    asyncGetInitLocations
  };
};
